/**
 * Profile (Sprint 26) — page "Hồ sơ".
 *
 * Read-only: hiện Program + Major mà student đang/đã theo, suy ra qua
 * `useMyMajors()` (KHÔNG có bảng UserProgram riêng — program dedupe
 * client-side từ `major.program`).
 */

import { Breadcrumb, Card, Empty, List, Result, Space, Spin, Tag, Typography } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { useMyMajors, type MajorEnrollmentStatus } from "@/api/enrollment";
import { formatDate } from "@/lib/formatDate";

const { Title, Text, Paragraph } = Typography;

const STATUS_LABEL: Record<MajorEnrollmentStatus, string> = {
  enrolled: "Đang theo học",
  completed: "Đã tốt nghiệp ngành",
  dropped: "Đã rời ngành",
};

const STATUS_COLOR: Record<MajorEnrollmentStatus, string> = {
  enrolled: "processing",
  completed: "success",
  dropped: "default",
};

export function Profile() {
  const user = useAuthStore((s) => s.user);
  const { data: majors, isLoading, error } = useMyMajors();

  // Dedupe program từ các major đã gán — FE-side, không gọi API riêng.
  const programs = Array.from(
    new Map((majors ?? []).map((m) => [m.major.program.id, m.major.program])).values(),
  );

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[{ title: <Link to="/"><HomeOutlined /></Link> }, { title: "Hồ sơ" }]}
      />

      <Title level={2}>Hồ sơ</Title>
      {user && (
        <Paragraph type="secondary">
          {user.email}
        </Paragraph>
      )}

      <Card title="Chương trình đào tạo" style={{ marginBottom: 16 }}>
        {programs.length === 0 && !isLoading ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Bạn chưa được gán vào chương trình đào tạo nào."
          />
        ) : (
          <List
            dataSource={programs}
            renderItem={(p) => (
              <List.Item key={p.id}>
                <Text strong>{p.name}</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  ({p.code})
                </Text>
              </List.Item>
            )}
          />
        )}
      </Card>

      <Card title="Ngành học">
        {isLoading && (
          <div style={{ textAlign: "center", padding: 32 }}>
            <Spin />
          </div>
        )}

        {error && <Result status="error" title="Không tải được danh sách ngành học" />}

        {!isLoading && majors && majors.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Bạn chưa được gán vào ngành học nào. Liên hệ quản trị viên để được xếp ngành."
          />
        )}

        {!isLoading && majors && majors.length > 0 && (
          <List
            dataSource={majors}
            renderItem={(m) => (
              <List.Item key={m.id}>
                <Space direction="vertical" size={0}>
                  <Link to={`/majors/${m.major.slug}`}>
                    <Text strong>{m.major.name}</Text>
                  </Link>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {m.major.program.name} · Gán ngày {formatDate(m.enrolled_at)}
                  </Text>
                </Space>
                <Tag color={STATUS_COLOR[m.status]} style={{ marginLeft: "auto" }}>
                  {STATUS_LABEL[m.status]}
                </Tag>
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
}
